import type {
  Collection,
  CollectionConnection,
  CollectionEdge,
  Metafield,
  Product,
  ProductConnection,
  ProductEdge,
} from "@shopify/hydrogen-react/storefront-api-types";
import { query } from "gql-query-builder";

import { APP_NAME, filterFields, imageFields, pageFields, productFields } from "./fields";
import { brandsMetafield, membershipMetafield, tagMetafield, typeMetafield } from "./metafields/collection-metafields";
import type { IProductMetafields } from "./product-gql";
import { commonCollectionProductFields, commonProductFields } from "./product-gql";
import { dropDateMetafield, launchMetafield, thumbnailImagesMetafield } from "./metafields/product-metafields";
import { relatedProductsMetafield } from "./metafields/blog-metafields";
import type IQueryBuilderOptions from "gql-query-builder/build/IQueryBuilderOptions";

export interface ICollectionMetafields {
  accessible_memberships?: Metafield;
}

/**
 * handle 이용하여 컬렉션 상품 목록 조회
 */
export interface ICollectionHandleQuery {
  collection: Collection &
    ICollectionMetafields & {
      pages: ProductConnection;
      products: ProductConnection & {
        edges: (ProductEdge & { node: Product & IProductMetafields })[];
      };
    };
}

export const collectionHandleQuery = query(
  {
    operation: "collection",
    fields: [
      "id",
      "title",
      "description",
      {
        operation: "pages: products(first: 1)",
        fields: [{ edges: [{ node: ["id"] }] }],
        variables: {},
      },
      "handle",
      { image: imageFields },
      {
        operation: "products",
        fields: [
          {
            filters: filterFields,
            edges: [{ node: [...commonProductFields] }],
          },
          { pageInfo: pageFields },
        ],
        variables: {
          first: { type: "Int!" },
          after: { type: "String" },
          filters: { type: "[ProductFilter!]" },
          sortKey: { type: "ProductCollectionSortKeys!" },
          reverse: { type: "Boolean" },
        },
      },
      membershipMetafield,
    ],
    variables: { handle: { type: "String!" } },
  },
  undefined,
  { operationName: "CollectionHandle" },
);

/**
 * handle 이용하여 컬렉션 상품 목록 조회 (QuickAdd 경량용)
 */
export const collectionQuickAddQuery = query(
  {
    operation: "collection",
    fields: [
      "id",
      "title",
      "handle",
      {
        operation: "products",
        fields: [
          {
            filters: filterFields,
            edges: [{ node: [...commonCollectionProductFields] }],
          },
          { pageInfo: pageFields },
        ],
        variables: {
          first: { type: "Int!" },
          after: { type: "String" },
          filters: { type: "[ProductFilter!]" },
          sortKey: { type: "ProductCollectionSortKeys!" },
          reverse: { type: "Boolean" },
        },
      },
    ],
    variables: { handle: { type: "String!" } },
  },
  undefined,
  { operationName: "CollectionQuickAdd" },
);

/**
 * 컬렉션 목록 조회
 */
export interface ICollectionsEdgesNode extends Collection {
  brands?: Metafield;
  type?: Metafield;
  tag?: Metafield;
}

export interface ICollectionsQuery {
  collections: CollectionConnection & {
    edges: (CollectionEdge & { node: ICollectionsEdgesNode })[];
  };
}

export const collectionsQuery = query(
  {
    operation: "collections(first: 250, sortKey: TITLE)",
    fields: [{ edges: [{ node: ["id", "title", "handle", brandsMetafield, typeMetafield, tagMetafield] }] }],
  },
  undefined,
  { operationName: "CollectionsTitle" },
);

const productMetafields: Record<string, IQueryBuilderOptions[]> = {
  tune: [dropDateMetafield],
  yeseyesee: [],
  thisisneverthat: [],
  khakis: [],
  sunlove: [],
  wellbeingexpress: [],
};

export const collectionsTitleQuery = query(
  {
    operation: "collections",
    fields: [
      {
        edges: [
          {
            node: [
              "id",
              "title",
              "handle",
              { image: imageFields },
              {
                operation: "products(first:250)",
                fields: [{ edges: [{ node: [...productFields, ...productMetafields[APP_NAME]] }] }],
                variables: {},
              },
            ],
          },
        ],
      },
    ],
    variables: {
      first: { type: "Int!" },
      query: { type: "String" },
    },
  },
  undefined,
  { operationName: "CollectionsByTitle" },
);

/**
 * 컬렉션 filters(count) 조회
 */
export interface ICollectionsFiltersQuery {
  collections: CollectionConnection & {
    edges: (CollectionEdge & { node: Collection & { products: ProductConnection } })[];
  };
}

export const collectionsFiltersQuery = query(
  {
    operation: "collections(first: 250)",
    fields: [
      {
        edges: [
          {
            node: [
              "id",
              "title",
              "handle",
              {
                operation: "products",
                fields: [{ filters: filterFields }],
                variables: { first: { type: "Int!" } },
              },
            ],
          },
        ],
      },
    ],
  },
  undefined,
  { operationName: "CollectionFilters" },
);

export interface ICollectionQuery {
  collection: Collection & {
    products: { edges: { node: Product & IProductMetafields }[] };
  };
}

export const collectionQuery = query(
  {
    operation: "collection",
    fields: [
      {
        operation: "Collection",
        fields: [
          "id",
          "handle",
          {
            operation: "products",
            fields: [
              {
                edges: [
                  {
                    node: [
                      ...productFields,
                      launchMetafield,
                      thumbnailImagesMetafield,
                      relatedProductsMetafield,
                      dropDateMetafield,
                    ],
                  },
                ],
              },
              { pageInfo: pageFields },
            ],
            variables: {
              first: { type: "Int" },
              after: { type: "String" },
              filters: { type: "[ProductFilter!]" },
              sortKey: { type: "ProductCollectionSortKeys" },
              reverse: { type: "Boolean" },
            },
          },
        ],
        fragment: true,
      },
    ],
    variables: {
      handle: { type: "String" },
    },
  },
  undefined,
  { operationName: "Collection" },
);
